import { UtilityHelper } from '@/helpers/UtilityHelper';
import i18n from '@/i18n';
import store from '@/store';
import { Action } from '@/constants/ajax';
import { FieldInterface } from '@/types';
import * as TrackingSDK from '@oanda/platforms-up-tracking-sdk';
import { Region } from '@oanda/platforms-up-tracking-sdk/dist/types';

declare global {
    interface Window {
        PlatformsTracking: typeof TrackingSDK;
    }
}

const LS_COUNTRY_KEY = 'visitor_country_name';
const LS_COUNTRY_CODE_KEY = 'visitor_country_code';
const DEFAULT_COUNTRY_NAME = 'Ireland';

export interface OptimoveIdentifyData {
    email?: string | null;
    branch_code?: string | null;
    region?: string | null;
    language?: string | null;
    country?: string | null;
}

export interface OptimoveTrackData {
    email?: string | null;
    branch_code?: string | null;
    region?: string | null;
    language?: string | null;
    form_name?: string | null;
    source?: string | null;
    form_source?: string | null;
    country?: string | null;
}

const getLanguage = (dataLanguage?: string | null): string => {
    const locale = (i18n?.global?.locale?.value ?? 'en').toString();
    return dataLanguage ?? `${locale}-${locale.toUpperCase()}`;
};

const safeGetLS = (key: string): string | null => {
    try {
        const v = window.localStorage.getItem(key);
        return v && v.trim() ? v : null;
    } catch {
        return null;
    }
};

const safeSetLS = (key: string, value: string): void => {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // ignore
    }
};

const fetchClientCountryCode = async (): Promise<string> => {
    try {
        const ajaxService = store.state.ajaxService;
        const response = (await ajaxService.post(
            ajaxService.RESOURCES[Action.GET_CLIENT_COUNTRY],
            {
                token: ajaxService.getToken(),
            }
        )) as { country_code?: string };
        return (response?.country_code ?? '').trim();
    } catch {
        return '';
    }
};

const getCountryNameByCode = (code: string): string => {
    const countries = store.state.config.countries as FieldInterface[];
    const match = countries.find((c) => String(c.key) === String(code));
    return match ? String(match.value) : '';
};

/**
 * Resolves country name with caching:
 * - localStorage visitor_country_name
 * - localStorage visitor_country_code -> lookup name in state config countries
 * - fetch country code from Action.GET_CLIENT_COUNTRY -> store code + name
 */
const resolveCountryName = async (): Promise<string> => {
    const cachedCountry = safeGetLS(LS_COUNTRY_KEY);
    if (cachedCountry) return cachedCountry;

    let countryCode = safeGetLS(LS_COUNTRY_CODE_KEY);
    if (!countryCode) {
        countryCode = await fetchClientCountryCode();
        if (countryCode) safeSetLS(LS_COUNTRY_CODE_KEY, countryCode);
    }

    const country = countryCode ? getCountryNameByCode(countryCode) : '';
    if (country) safeSetLS(LS_COUNTRY_KEY, country);

    return country || DEFAULT_COUNTRY_NAME;
};

/* ------------------------------------------
 * SDK init + "identify gating" (shared state)
 * ------------------------------------------ */

let sdkReady = false;

let identifyBarrier: Promise<void> | null = null;
let identifyBarrierResolve: (() => void) | null = null;
let identifyBarrierReject: ((e: unknown) => void) | null = null;

let lastIdentifiedEmail: string | null = null;

/**
 * Creates (or reuses) a barrier Promise that resolves when identify is "done".
 * We resolve it after we call identify + a microtask delay.
 *
 * Note: if the SDK has a real identify callback/event, resolve there instead.
 */
const beginIdentifyBarrier = (): Promise<void> => {
    if (identifyBarrier) return identifyBarrier;

    identifyBarrier = new Promise<void>((resolve, reject) => {
        identifyBarrierResolve = resolve;
        identifyBarrierReject = reject;
    });

    return identifyBarrier;
};

const resolveIdentifyBarrier = (): void => {
    identifyBarrierResolve?.();
    identifyBarrierResolve = null;
    identifyBarrierReject = null;
};

const rejectIdentifyBarrier = (e: unknown): void => {
    identifyBarrierReject?.(e);
    identifyBarrierResolve = null;
    identifyBarrierReject = null;
};

const waitForIdentifyIfNeeded = async (email: string): Promise<void> => {
    // If we already identified the same email (in this SPA session), no wait needed.
    if (lastIdentifiedEmail && lastIdentifiedEmail === email) return;

    // If identify is in-flight, wait for it.
    if (identifyBarrier) {
        await identifyBarrier;
    }
};

export const initOptimove = (): void => {
    window.PlatformsTracking = TrackingSDK;
    window.PlatformsTracking.setEnviroment('production');
    window.PlatformsTracking.page();
    sdkReady = true;
};

/**
 * Identify: starts/updates the barrier so other files can safely track after it.
 */
export const optimoveIdentifyEvent = async (
    data: OptimoveIdentifyData
): Promise<void> => {
    if (!sdkReady) {
        // Optionally: initOptimove(); if you want auto-init
        console.warn(
            'Optimove SDK not initialized. Call initOptimove() first.'
        );
    }

    const email = (data.email ?? '').trim();
    if (!email) return;

    // Start barrier early so concurrent track calls will wait.
    beginIdentifyBarrier();

    try {
        const language = getLanguage(data.language);
        const country = await resolveCountryName();

        const identifyPayload = {
            email,
            branch_code: data.branch_code ?? 'EU',
            region: 'EU' as Region,
            language,
            country,
        };

        window.PlatformsTracking?.identify(identifyPayload);

        // If SDK has no callback, we at least sequence within the JS turn.
        // This ensures any immediate track calls in other files that awaited the barrier
        // will run after identify() has been invoked.
        await Promise.resolve();

        lastIdentifiedEmail = email;
        resolveIdentifyBarrier();
    } catch (e) {
        rejectIdentifyBarrier(e);
        // Reset barrier so future attempts can retry.
        identifyBarrier = null;
        throw e;
    }
};

/**
 * Track: waits for identify (if needed) even when called from a different file.
 */
export const optimoveTrackEvent = async (
    data: OptimoveTrackData
): Promise<void> => {
    if (!sdkReady) {
        console.warn(
            'Optimove SDK not initialized. Call initOptimove() first.'
        );
    }

    const email = (data.email ?? '').trim();
    if (!email) return;

    // Wait for identify if it's in-flight or not yet completed for this email.
    await waitForIdentifyIfNeeded(email);

    const language = getLanguage(data.language);
    const country = await resolveCountryName();
    const source =
        data.form_source || data.source || UtilityHelper.isOandaPath()
            ? 'OANDAcom_OpenReal_RODO'
            : 'TMSpl_OpenReal_RODO';

    const trackPayload = {
        form_name: data.form_name ?? 'onboarding',
        email,
        source: source,
        form_source: source,
        branch_code: data.branch_code ?? 'EU',
        language,
        region: 'EU' as Region,
        country,
    };

    window.PlatformsTracking?.track('tms_campaign_assigned', trackPayload);
};
